import React, { useState } from 'react';
import { Download, ZoomIn, ZoomOut } from 'lucide-react';
import { useAuth } from '../../auth/hooks/auth';
import type { UserRole } from '../sharedComponents/AuthProvider';
import AdminManual from "../../assets/manuals/TestAdmin.pdf";
import AuthorManual from "../../assets/manuals/TestAuthor.pdf";
import AccessorManual from "../../assets/manuals/TestAccessor.pdf";


const MANUAL_BY_ROLE: Record<string, { file: string; label: string }> = {
  author: {
    file: AuthorManual,
    label: 'Author User Manual',
  },
  accessor: {
    file: AccessorManual,
    label: 'Reviewer User Manual',
  },
};

const ADMIN_MANUAL = {
  file: AdminManual,
  label: 'Administrator User Manual',
};

const ADMIN_ROLES: UserRole[] = ['admin', 'systemadmin', 'tagadmin', 'superadmin'];

function getManualForRole(role: UserRole | undefined) {
  if (!role) return ADMIN_MANUAL;
  if (ADMIN_ROLES.includes(role)) return ADMIN_MANUAL;
  return MANUAL_BY_ROLE[role] ?? ADMIN_MANUAL;
}

const UserManual: React.FC = () => {
  const { user } = useAuth();
  const manual = getManualForRole(user?.role);

  const [scale, setScale] = useState(100);

  const zoomIn  = () => setScale(prev => Math.min(prev + 10, 200));
  const zoomOut = () => setScale(prev => Math.max(prev - 10, 50));

  return (
    <div className="w-full">
        <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
            
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                    User Manual
                </h1>
                <p className="text-gray-500 text-sm sm:text-base">
                    Step-by-step guidance tailored to your role —{' '}
                    <span className="font-medium text-gray-700">
                        {manual.label}
                    </span>
                </p>
            </div>

        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">

            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                <span className="text-sm font-medium text-gray-700 truncate max-w-xs">
                    {manual.label}
                </span>

                <div className="flex items-center gap-2">
                    <button
                    onClick={zoomOut}
                    disabled={scale <= 50}
                    className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-40 transition-colors"
                    title="Zoom out"
                    >
                    <ZoomOut className="w-4 h-4 text-gray-600" />
                    </button>

                    <span className="text-xs text-gray-500 w-12 text-center">{scale}%</span>

                    <button
                    onClick={zoomIn}
                    disabled={scale >= 200}
                    className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-40 transition-colors"
                    title="Zoom in"
                    >
                    <ZoomIn className="w-4 h-4 text-gray-600" />
                    </button>

                    <div className="w-px h-5 bg-gray-200 mx-1" />

                    {/* Download */}
                    <a
                    href={manual.file}
                    download
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors"
                    >
                    <Download className="w-3.5 h-3.5" />
                    Download
                    </a>
                </div>
            </div>

            <div
                className="relative bg-gray-100 p-2"
                style={{ height: 'calc(100vh - 260px)', minHeight: '480px' }}
            >
                <iframe
                    key={manual.file}         
                    src={`${manual.file}#toolbar=0&navpanes=0&scrollbar=1`}
                    title={manual.label}
                    className="w-full h-full border-0 transition-all duration-200"
                    style={{ transform: `scale(${scale / 100})`, transformOrigin: 'top left', width: `${10000 / scale}%`, height: `${10000 / scale}%` }}
                />
            </div>

            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-400 text-center">
                Can't see the document?&nbsp;
                <a href={manual.file} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
                    Open in a new tab
                </a>
                &nbsp;or use the Download button above.
            </div>
        </div>
    </div>
  );
};

export default UserManual;